import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  LogOut, 
  LayoutDashboard, 
  Package, 
  ShoppingCart, 
  Settings, 
  Trophy,
  Target,
  User,
  Image,
  Users,
  Music,
  DollarSign,
  Bell,
  Crown,
  Swords,
  Building2,
  Gift,
  Wallet,
  Palette,
  ShieldCheck,
  ShieldAlert
} from 'lucide-react';

// Vou importar os componentes conforme criá-los
import DashboardStats from '../components/admin/DashboardStats';
import PackagesManagement from '../components/admin/PackagesManagement';
import OrdersManagement from '../components/admin/OrdersManagement';
import DisputesManagement from '../components/admin/DisputesManagement';
import TasksManagement from '../components/admin/TasksManagement';
import RankingManagement from '../components/admin/RankingManagement';
import SystemConfig from '../components/admin/SystemConfig';
import AdvertisementsManagement from '../components/admin/AdvertisementsManagement';
import ResellersManagement from '../components/admin/ResellersManagement';
import PlaylistManagement from '../components/admin/PlaylistManagement';
import FinancesManagement from '../components/admin/FinancesManagement';
import ClientsManagement from '../components/admin/ClientsManagement';
import MegaPrizeManagement from '../components/admin/MegaPrizeManagement';
import TelegramConfig from '../components/admin/TelegramConfig';
import HostEventManagement from '../components/admin/HostEventManagement';
import ResellerNoticesManagement from '../components/admin/ResellerNoticesManagement';
import PKBattleManagement from '../components/admin/PKBattleManagement';
import AgencyManagement from '../components/admin/AgencyManagement';
import OilTycoonManagement from '../components/admin/OilTycoonManagement';
import WitchBalloonManagement from '../components/admin/WitchBalloonManagement';
import RecargaPremiadaManagement from '../components/admin/RecargaPremiadaManagement';
import GbArenaManagement from '../components/admin/GbArenaManagement';
import MysteryBoxManagement from '../components/admin/MysteryBoxManagement';
import AffiliateWalletManagement from '../components/admin/AffiliateWalletManagement';
import BlueCoinsManagement from '../components/admin/BlueCoinsManagement';
import RouletteCampaignManagement from '../components/admin/RouletteCampaignManagement';
import ThemeManagement from '../components/admin/ThemeManagement';
import KakoAuthManagement from '../components/admin/KakoAuthManagement';
import TokenExpiryBanner from '../components/admin/TokenExpiryBanner';
import { ordersAPI, rouletteAdminAPI, disputesAPI } from '../services/api';
import MyProfile from './MyProfile';

const AdminDashboard = () => {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [paymentAlerts, setPaymentAlerts] = useState(null);
  const [disputeAlerts, setDisputeAlerts] = useState(null);
  const [prizeAlerts, setPrizeAlerts] = useState(null);

  // Sinal vermelho no menu: pedido PAGO sem crédito (checagem a cada 60s)
  useEffect(() => {
    let active = true;
    const loadAlerts = async () => {
      try {
        const response = await ordersAPI.paymentAlerts();
        if (active) setPaymentAlerts(response.data);
      } catch (error) {
        // silencioso: alerta não pode quebrar o painel
      }
      try {
        const r = await rouletteAdminAPI.prizeAlerts();
        if (active) setPrizeAlerts(r.data);
      } catch (error) {
        // silencioso
      }
      try {
        const d = await disputesAPI.summary();
        if (active) setDisputeAlerts(d.data);
      } catch (error) {
        // silencioso
      }
    };
    loadAlerts();
    const timer = setInterval(loadAlerts, 60000);
    return () => { active = false; clearInterval(timer); };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-purple-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">🦊 FOX SELLER - Admin</h1>
              <p className="text-sm opacity-90">Bem-vindo, {admin?.username}</p>
            </div>
            <Button
              data-testid="logout-button"
              onClick={handleLogout}
              variant="outline"
              className="bg-white/10 hover:bg-white/20 border-white/30 text-white"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sair
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-2 sm:px-4 py-4 sm:py-6">
        <TokenExpiryBanner onGoToAgency={() => setActiveTab('agency')} />
        {paymentAlerts?.alert && (
          <button
            data-testid="payment-alert-banner"
            onClick={() => setActiveTab('orders')}
            className="w-full mb-3 flex items-center gap-3 rounded-xl border-2 border-red-500 bg-red-50 px-4 py-3 text-left transition-colors hover:bg-red-100"
          >
            <span className="relative flex h-3 w-3 flex-shrink-0">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
              <span className="relative inline-flex h-3 w-3 rounded-full bg-red-600" />
            </span>
            <span className="text-sm text-red-900">
              <b>
                {(paymentAlerts.uncredited ?? 0) > 0
                  ? `${paymentAlerts.uncredited} pedido(s) pago(s) sem crédito`
                  : `${paymentAlerts.manual_review} pedido(s) precisam de revisão manual`}
              </b>
              {' · '}
              {paymentAlerts.cancelled_paid > 0 && `${paymentAlerts.cancelled_paid} cancelado(s) pago(s) · `}
              {paymentAlerts.failed_paid > 0 && `${paymentAlerts.failed_paid} com falha de crédito · `}
              {paymentAlerts.paid_stuck > 0 && `${paymentAlerts.paid_stuck} travado(s) · `}
              {(paymentAlerts.uncredited ?? 0) > 0 && paymentAlerts.manual_review > 0 && `${paymentAlerts.manual_review} em revisão manual · `}
              clique para abrir a Integridade de Pagamentos
            </span>
          </button>
        )}
        {prizeAlerts?.alert && (
          <button
            data-testid="roulette-alert-banner"
            onClick={() => setActiveTab('roulette')}
            className="w-full mb-3 flex items-center gap-3 rounded-xl border-2 border-red-500 bg-red-50 px-4 py-3 text-left transition-colors hover:bg-red-100"
          >
            <span className="relative flex h-3 w-3 flex-shrink-0">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
              <span className="relative inline-flex h-3 w-3 rounded-full bg-red-600" />
            </span>
            <span className="text-sm text-red-900">
              <b>{prizeAlerts.stuck_total} prêmio(s) da roleta sem chegar na conta do ganhador</b>
              {prizeAlerts.manual_review > 0 && ` · ${prizeAlerts.manual_review} parado(s) de vez`}
              {' · '}clique para abrir a Roleta e reprocessar
            </span>
          </button>
        )}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          {/* Tabs com scroll horizontal em mobile */}
          <div className="overflow-x-auto -mx-2 px-2 sm:mx-0 sm:px-0">
            <TabsList className="inline-flex w-auto min-w-full sm:min-w-0 flex-nowrap">
            <TabsTrigger value="dashboard" className="flex-shrink-0">
              <LayoutDashboard className="w-4 h-4 mr-2" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="packages" className="flex-shrink-0">
              <Package className="w-4 h-4 mr-2" />
              Pacotes
            </TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders" className="flex-shrink-0">
              <ShoppingCart className="w-4 h-4 mr-2" />
              Pedidos
              {paymentAlerts?.alert && (
                <span
                  data-testid="orders-alert-badge"
                  title="Pedidos pagos sem crédito — abra a Integridade de Pagamentos"
                  className="ml-2 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white animate-pulse"
                >
                  {paymentAlerts.total}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="tasks" className="flex-shrink-0">
              <Target className="w-4 h-4 mr-2" />
              Tarefas
            </TabsTrigger>
            <TabsTrigger value="ranking" className="flex-shrink-0">
              <Trophy className="w-4 h-4 mr-2" />
              Ranking
            </TabsTrigger>
            <TabsTrigger value="resellers" className="flex-shrink-0">
              <Users className="w-4 h-4 mr-2" />
              Afiliados
            </TabsTrigger>
            <TabsTrigger value="affiliate-wallet" data-testid="tab-affiliate-wallet" className="flex-shrink-0">
              <Wallet className="w-4 h-4 mr-2" />
              Carteira Afiliados
            </TabsTrigger>
            <TabsTrigger value="bluecoins" data-testid="tab-blue-coins" className="flex-shrink-0">
              <Wallet className="w-4 h-4 mr-2" />
              Azulzinhas 💙
            </TabsTrigger>
            <TabsTrigger value="roulette" data-testid="tab-roulette" className="flex-shrink-0">
              <Wallet className="w-4 h-4 mr-2" />
              Roleta 🎡
              {prizeAlerts?.alert && (
                <span
                  data-testid="roulette-alert-badge"
                  title="Prêmio sorteado que ainda não chegou na conta do ganhador"
                  className="ml-2 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white animate-pulse"
                >
                  {prizeAlerts.stuck_total}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="clients" data-testid="tab-clients" className="flex-shrink-0">
              <Users className="w-4 h-4 mr-2" />
              Clientes
            </TabsTrigger>
            <TabsTrigger value="ads" className="flex-shrink-0">
              <Image className="w-4 h-4 mr-2" />
              Anúncios
            </TabsTrigger>
            <TabsTrigger value="playlist" data-testid="tab-playlist" className="flex-shrink-0">
              <Music className="w-4 h-4 mr-2" />
              Playlist
            </TabsTrigger>
            <TabsTrigger value="finances" data-testid="tab-finances" className="flex-shrink-0">
              <DollarSign className="w-4 h-4 mr-2" />
              Finanças
            </TabsTrigger>
            <TabsTrigger value="megaprize" className="flex-shrink-0">
              <Trophy className="w-4 h-4 mr-2" />
              MEGA PRÊMIO
            </TabsTrigger>
            <TabsTrigger value="hostevent" className="flex-shrink-0">
              <Crown className="w-4 h-4 mr-2" />
              MEU HOST PREFERIDO
            </TabsTrigger>
            <TabsTrigger value="pkbattle" className="flex-shrink-0">
              <Swords className="w-4 h-4 mr-2" />
              BATALHA DE PK
            </TabsTrigger>
            <TabsTrigger value="agency" className="flex-shrink-0">
              <Building2 className="w-4 h-4 mr-2" />
              MINHA AGÊNCIA
            </TabsTrigger>
            <TabsTrigger value="mysterybox" className="flex-shrink-0">
              <Gift className="w-4 h-4 mr-2" />
              BAÚ MISTERIOSO
            </TabsTrigger>
            <TabsTrigger value="oiltycoon" data-testid="tab-oiltycoon" className="flex-shrink-0">
              🛢️ OIL TYCOON
            </TabsTrigger>
            <TabsTrigger value="witchballoon" data-testid="tab-witchballoon" className="flex-shrink-0">
              🎈 WITCH'S BALLOON
            </TabsTrigger>
            <TabsTrigger value="recargapremiada" data-testid="tab-recargapremiada" className="flex-shrink-0">
              🎁 RECARGA PREMIADA
            </TabsTrigger>
            <TabsTrigger value="gbarena" data-testid="tab-gbarena" className="flex-shrink-0">
              🎃 GB ARENA
            </TabsTrigger>
            <TabsTrigger value="theme" data-testid="tab-theme" className="flex-shrink-0">
              <Palette className="w-4 h-4 mr-2" />
              Tema
            </TabsTrigger>
            <TabsTrigger value="kako-auth" data-testid="tab-kako-auth" className="flex-shrink-0">
              <ShieldCheck className="w-4 h-4 mr-2" />
              Autenticador
            </TabsTrigger>
            <TabsTrigger value="telegram" className="flex-shrink-0">
              <Bell className="w-4 h-4 mr-2" />
              Telegram
            </TabsTrigger>
            <TabsTrigger value="config" className="flex-shrink-0">
              <Settings className="w-4 h-4 mr-2" />
              Config
            </TabsTrigger>
            <TabsTrigger value="profile" className="flex-shrink-0">
              <User className="w-4 h-4 mr-2" />
              Perfil
            </TabsTrigger>
          </TabsList>
          </div>
          {/* Fim do wrapper de scroll */}

          <TabsContent value="dashboard" className="space-y-4">
            <DashboardStats />
          </TabsContent>

          <TabsContent value="packages" className="space-y-4">
            <PackagesManagement />
          </TabsContent>

          <TabsContent value="orders" className="space-y-4">
            <Tabs defaultValue="lista" className="space-y-4">
              <TabsList data-testid="orders-subtabs">
                <TabsTrigger value="lista" data-testid="subtab-orders-list">
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Pedidos
                </TabsTrigger>
                <TabsTrigger value="contestacoes" data-testid="subtab-disputes">
                  <ShieldAlert className="w-4 h-4 mr-2" />
                  Contestações
                  {disputeAlerts?.alert && (
                    <span
                      data-testid="disputes-alert-badge"
                      title="Contestação aberta ou cliente bloqueado"
                      className="ml-2 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white animate-pulse"
                    >
                      {(disputeAlerts.open || 0) + (disputeAlerts.blocked || 0)}
                    </span>
                  )}
                </TabsTrigger>
              </TabsList>
              <TabsContent value="lista">
                <OrdersManagement />
              </TabsContent>
              <TabsContent value="contestacoes">
                <DisputesManagement />
              </TabsContent>
            </Tabs>
          </TabsContent>

          <TabsContent value="tasks" className="space-y-4">
            <TasksManagement />
          </TabsContent>

          <TabsContent value="ranking" className="space-y-4">
            <RankingManagement />
          </TabsContent>

          <TabsContent value="resellers" className="space-y-4">
            <ResellersManagement />
          </TabsContent>

          <TabsContent value="affiliate-wallet" className="space-y-4">
            <AffiliateWalletManagement />
          </TabsContent>

          <TabsContent value="bluecoins" className="space-y-4">
            <BlueCoinsManagement />
          </TabsContent>

          <TabsContent value="roulette" className="space-y-4">
            <RouletteCampaignManagement />
          </TabsContent>

          <TabsContent value="clients" className="space-y-4">
            <ClientsManagement />
          </TabsContent>

          <TabsContent value="ads" className="space-y-4">
            <AdvertisementsManagement />
          </TabsContent>

          <TabsContent value="playlist" className="space-y-4">
            <PlaylistManagement />
          </TabsContent>

          <TabsContent value="finances" className="space-y-4">
            <FinancesManagement />
          </TabsContent>

          <TabsContent value="megaprize" className="space-y-4">
            <MegaPrizeManagement />
          </TabsContent>

          <TabsContent value="hostevent" className="space-y-4">
            <HostEventManagement />
          </TabsContent>

          <TabsContent value="pkbattle" className="space-y-4">
            <PKBattleManagement />
          </TabsContent>

          <TabsContent value="agency" className="space-y-4">
            <AgencyManagement />
          </TabsContent>

          <TabsContent value="oiltycoon" className="space-y-4">
            <OilTycoonManagement />
          </TabsContent>

          <TabsContent value="witchballoon" className="space-y-4">
            <WitchBalloonManagement />
          </TabsContent>

          <TabsContent value="recargapremiada" className="space-y-4">
            <RecargaPremiadaManagement />
          </TabsContent>

          <TabsContent value="gbarena" className="space-y-4">
            <GbArenaManagement />
          </TabsContent>

          <TabsContent value="mysterybox" className="space-y-4">
            <MysteryBoxManagement />
          </TabsContent>

          <TabsContent value="theme" className="space-y-4">
            <ThemeManagement />
          </TabsContent>

          <TabsContent value="kako-auth" className="space-y-4">
            <KakoAuthManagement />
          </TabsContent>

          <TabsContent value="telegram" className="space-y-4">
            <TelegramConfig />
          </TabsContent>

          <TabsContent value="config" className="space-y-4">
            <SystemConfig />
          </TabsContent>

          <TabsContent value="profile" className="space-y-4">
            <MyProfile />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
